// Boot and lifetime of the session: read the token from the URL, exchange it, keep the result in memory, renew near expiry.
import { Theme, UiLang, type AccountSignedIn, type SsoExchangeResponse } from "@imagora/contracts";
import { z } from "zod";
import { ApiError } from "./api";
import type { HostBridge } from "./host-bridge";

export type Session = SsoExchangeResponse;

type CookieOutcome = { kind: "ok" } | { kind: "out" } | { kind: "unreachable" } | { kind: "refused"; code: string };

export type SessionState =
  | { status: "booting" }
  | { status: "ready"; session: Session }
  /** Signed in here, with no workspace yet: the workspace form comes next. */
  | { status: "account"; account: AccountSignedIn }
  /** Opened directly, without a token and with no parent to ask. */
  | { status: "none" }
  /** A browser that stays signed in could not reach the API on a reload (a deploy, a 502, no network): the device stays, and it tries again. */
  | { status: "unreachable" }
  | { status: "error"; code: string };

/** A person who signed in on a host's domain rather than arriving from a host: the token outlives a reload, not the tab. */
export interface DirectSessionStorage {
  load(): string | null;
  save(token: string | null): void;
  restore(token: string): Promise<AccountSignedIn>;
}

/**
 * A person who signed in on the service's own address: the browser stays signed in for 30 days through a cookie the page cannot read.
 * The access token lives only in memory and is renewed through the cookie, before it ends and after a reload.
 */
export interface StayingSignedIn {
  /** Whether this browser was signed in to stay so. Only a hint, so a stranger's first visit does not knock on the refresh. */
  hinted(): boolean;
  hint(on: boolean): void;
  /** The workspace this tab is in, so a reload, and each tab, keeps its own. */
  tabTenant: { load(): string | null; save(id: string | null): void };
  refresh(input: { tenantId?: string; lang?: UiLang }): Promise<AccountSignedIn>;
}

/** What a push subscription leaves behind: a way in when a notice is tapped, for a person with no letter to hand and maybe no sign-in of their own. */
export interface DeviceSessionStorage {
  /** The token for this business; with none named, whatever the browser allows without one having been tapped. */
  load(tenantId: string | null): string | null;
  forget(token: string): void;
  open(token: string): Promise<Session>;
}

export interface SessionDeps {
  exchange: (token: string) => Promise<Session>;
  bridge: HostBridge;
  direct?: DirectSessionStorage;
  staying?: StayingSignedIn;
  device?: DeviceSessionStorage;
  now?: () => number;
  /** How long the parent gets to answer imagora:need-token. */
  tokenTimeoutMs?: number;
  /** How long before expiry the parent is asked for a new token. */
  renewBeforeMs?: number;
  /** A floor, so a host handing out short sessions cannot put us in a tight loop. */
  minRenewDelayMs?: number;
  /** Opens another client of an agency, with the session just renewed. */
  reopen?: (token: string, tenantId: string) => Promise<Session>;
  /** How soon a reload that could not reach the API tries again by itself. */
  resumeRetryMs?: number;
}

const DEFAULT_TOKEN_TIMEOUT_MS = 8000;
const DEFAULT_RENEW_BEFORE_MS = 5 * 60_000;
const DEFAULT_MIN_RENEW_DELAY_MS = 30_000;
const DEFAULT_RESUME_RETRY_MS = 5_000;

/** Only a 401 signs the device out. A network failure, a 5xx, a 408 or a 429 is the API out of reach for a moment. */
const transientFailure = (err: unknown) => !(err instanceof ApiError) || err.status === 0 || err.status === 408 || err.status === 429 || err.status >= 500;

/** A tapped notice says whose it is, for a phone signed in to more than one business. The address goes back to what the page is. */
export function takeWantedTenant(href: string): { tenant: string | null; cleanUrl: string | null } {
  const url = new URL(href);
  const tenant = url.searchParams.get("tenant");
  if (tenant === null) return { tenant: null, cleanUrl: null };
  url.searchParams.delete("tenant");
  return { tenant: tenant || null, cleanUrl: `${url.pathname}${url.search}${url.hash}` };
}

/** Finds `?sso=` or `#sso=` and returns the URL without it. The caller replaces the URL before anything else can read it. */
export function takeBootToken(href: string): { token: string | null; cleanUrl: string | null } {
  const url = new URL(href);
  const hash = new URLSearchParams(url.hash.startsWith("#") ? url.hash.slice(1) : url.hash);
  const fromQuery = url.searchParams.get("sso");
  const fromHash = hash.get("sso");
  if (!fromQuery && !fromHash) return { token: null, cleanUrl: null };
  url.searchParams.delete("sso");
  hash.delete("sso");
  url.hash = hash.toString();
  return { token: fromQuery ?? fromHash, cleanUrl: `${url.pathname}${url.search}${url.hash}` };
}

export function createSessionStore(deps: SessionDeps) {
  const now = deps.now ?? (() => Date.now());
  const tokenTimeoutMs = deps.tokenTimeoutMs ?? DEFAULT_TOKEN_TIMEOUT_MS;
  const renewBeforeMs = deps.renewBeforeMs ?? DEFAULT_RENEW_BEFORE_MS;
  const minRenewDelayMs = deps.minRenewDelayMs ?? DEFAULT_MIN_RENEW_DELAY_MS;
  const resumeRetryMs = deps.resumeRetryMs ?? DEFAULT_RESUME_RETRY_MS;

  let state: SessionState = { status: "booting" };
  let renewTimer: ReturnType<typeof setTimeout> | undefined;
  let resumeTimer: ReturnType<typeof setTimeout> | undefined;
  let resuming: Promise<CookieOutcome> | null = null;
  /** The workspace a tapped notice asked for at boot, kept for the retries of an unreachable reload. */
  let bootWanted: string | null = null;
  let refreshing: Promise<boolean> | null = null;
  let pendingToken: { resolve: (token: string | null) => void } | null = null;
  /** The client an agency person moved to inside a host's frame. The host only ever signs for the one it opened. */
  let openedTenantId: string | null = null;
  /** Whether the session in hand renews through the cookie. */
  let persistent = false;
  const listeners = new Set<() => void>();

  const setState = (next: SessionState) => {
    state = next;
    for (const listener of listeners) listener();
  };

  function scheduleRenewal(session: { expiresAt: string }) {
    clearTimeout(renewTimer);
    const delay = new Date(session.expiresAt).getTime() - now() - renewBeforeMs;
    // Browsers fire a timer longer than 2^31 ms at once, and a session this long means a clock problem anyway.
    if (delay > 2 ** 31 - 1) return;
    renewTimer = setTimeout(() => void refresh("expiring"), Math.max(minRenewDelayMs, delay));
    (renewTimer as { unref?: () => void }).unref?.();
  }

  async function adopt(token: string): Promise<void> {
    const session = await deps.exchange(token);
    if (!deps.bridge.trust(session.allowedOrigins)) throw new ApiError("embed_origin_not_allowed", 403);
    scheduleRenewal(session);
    setState({ status: "ready", session });
  }

  /** A token renewing the session opens the client that was open. If that client is no longer theirs, they land where the host signed them in. */
  async function adoptRenewed(token: string): Promise<void> {
    const opened = openedTenantId;
    const session = await deps.exchange(token);
    if (!deps.bridge.trust(session.allowedOrigins)) throw new ApiError("embed_origin_not_allowed", 403);
    let next = session;
    if (opened && opened !== session.tenant.id && deps.reopen) {
      try {
        next = await deps.reopen(session.token, opened);
      } catch {
        openedTenantId = null;
      }
    }
    scheduleRenewal(next);
    setState({ status: "ready", session: next });
  }

  /** A sign-in here, or the cookie's renewal of one. A session that does not stay signed in is kept for the tab, as before. */
  function adoptSignedIn(signedIn: AccountSignedIn): void {
    persistent = signedIn.persistent && Boolean(deps.staying);
    if (persistent) {
      deps.staying!.hint(true);
      deps.direct?.save(null);
    } else deps.direct?.save(signedIn.token);
    if (signedIn.session) deps.staying?.tabTenant.save(signedIn.session.tenant.id);
    if (persistent) scheduleRenewal(signedIn);
    else clearTimeout(renewTimer);
    setState(signedIn.session ? { status: "ready", session: signedIn.session } : { status: "account", account: signedIn });
  }

  /** The cookie's turn. Only a clear no ends the session; a phone with no signal keeps what it has and tries again. */
  async function refreshFromCookie(tenantId: string | null): Promise<CookieOutcome> {
    const lang = state.status === "ready" ? state.session.lang : state.status === "account" ? state.account.lang : undefined;
    try {
      adoptSignedIn(await deps.staying!.refresh({ ...(tenantId ? { tenantId } : {}), ...(lang ? { lang } : {}) }));
      return { kind: "ok" };
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        persistent = false;
        deps.staying!.hint(false);
        deps.staying!.tabTenant.save(null);
        clearTimeout(renewTimer);
        setState({ status: "none" });
        return { kind: "out" };
      }
      if (persistent) {
        clearTimeout(renewTimer);
        renewTimer = setTimeout(() => void refresh("expiring"), minRenewDelayMs);
        (renewTimer as { unref?: () => void }).unref?.();
      }
      return transientFailure(err) ? { kind: "unreachable" } : { kind: "refused", code: err instanceof ApiError ? err.code : "unknown" };
    }
  }

  /**
   * A reload of a browser that stays signed in: the cookie brings the session back, in the workspace this tab was in. When the API is out
   * of reach the device is kept, the page says so, and this runs again by itself; a refusal other than a 401 shows its error, and keeps it too.
   */
  function resumeFromCookie(): Promise<CookieOutcome> {
    clearTimeout(resumeTimer);
    resuming ??= (async () => {
      try {
        persistent = true;
        const outcome = await refreshFromCookie(bootWanted ?? deps.staying!.tabTenant.load());
        if (outcome.kind === "ok") {
          const current = state as SessionState;
          if (bootWanted && current.status === "ready" && current.session.tenant.id !== bootWanted) {
            const tapped = await openDevice(bootWanted);
            if (tapped) setState({ status: "ready", session: tapped });
          }
        } else if (outcome.kind === "unreachable") {
          // This timer, not the renewal's, brings the page back: it is sooner, and the page is waiting on it.
          clearTimeout(renewTimer);
          setState({ status: "unreachable" });
          resumeTimer = setTimeout(() => void resumeFromCookie(), resumeRetryMs);
          (resumeTimer as { unref?: () => void }).unref?.();
        } else if (outcome.kind === "refused") {
          clearTimeout(renewTimer);
          setState({ status: "error", code: outcome.code });
        } else persistent = false;
        return outcome;
      } finally {
        resuming = null;
      }
    })();
    return resuming;
  }

  function askParent(reason: "boot" | "expiring" | "expired"): Promise<string | null> {
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        pendingToken = null;
        resolve(null);
      }, tokenTimeoutMs);
      pendingToken = {
        resolve: (token) => {
          clearTimeout(timer);
          pendingToken = null;
          resolve(token);
        },
      };
      deps.bridge.askForToken(reason);
    });
  }

  deps.bridge.subscribe((message) => {
    if (message.type !== "imagora:token") return;
    if (pendingToken) pendingToken.resolve(message.token);
    else {
      // The host chose this one by itself, so it stands.
      openedTenantId = null;
      void adopt(message.token).catch(() => undefined);
    }
  });

  async function openDevice(tenantId: string | null): Promise<Session | null> {
    if (deps.bridge.embedded) return null;
    const token = deps.device?.load(tenantId);
    if (!token) return null;
    try {
      const session = await deps.device!.open(token);
      deps.direct?.save(session.token);
      return session;
    } catch (err) {
      // Only a clear no loses it. A phone with no signal keeps its way in for the next tap.
      if (err instanceof ApiError && err.status === 401) deps.device!.forget(token);
      return null;
    }
  }

  /** Asks the parent for a new token. Concurrent callers share one attempt. */
  function refresh(reason: "expiring" | "expired"): Promise<boolean> {
    if (!deps.bridge.embedded && persistent && deps.staying) {
      refreshing ??= (async () => {
        try {
          return (await refreshFromCookie(state.status === "ready" ? state.session.tenant.id : deps.staying!.tabTenant.load())).kind === "ok";
        } finally {
          refreshing = null;
        }
      })();
      return refreshing;
    }
    if (!deps.bridge.embedded) {
      if (reason !== "expired") return Promise.resolve(false);
      refreshing ??= (async () => {
        try {
          const session = await openDevice(state.status === "ready" ? state.session.tenant.id : null);
          if (session) {
            setState({ status: "ready", session });
            return true;
          }
          // Signed in here: the way back is the sign-in page, which the screen without a session offers.
          if (deps.direct?.load()) {
            deps.direct.save(null);
            setState({ status: "none" });
          } else setState({ status: "error", code: "session_expired" });
          return false;
        } finally {
          refreshing = null;
        }
      })();
      return refreshing;
    }
    refreshing ??= (async () => {
      try {
        const token = await askParent(reason);
        if (token) {
          await adoptRenewed(token);
          return true;
        }
      } catch {
        // Falls through: the current session is still good until the API says otherwise.
      } finally {
        refreshing = null;
      }
      if (reason === "expired") setState({ status: "error", code: "session_expired" });
      return false;
    })();
    return refreshing;
  }

  return {
    getState: () => state,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getToken: () => (state.status === "ready" ? state.session.token : state.status === "account" ? state.account.token : null),
    /** Called once. A parent that opened us without a token gets asked for one, which also covers a reload of the iframe. */
    async boot(token: string | null, wantedTenant: string | null = null): Promise<void> {
      try {
        const first = token ?? (deps.bridge.embedded ? await askParent("boot") : null);
        if (!first) {
          // A browser that stays signed in: the cookie brings the session back, in the workspace this tab was in.
          if (!deps.bridge.embedded && deps.staying?.hinted()) {
            bootWanted = wantedTenant;
            if ((await resumeFromCookie()).kind !== "out") return;
          }
          const saved = deps.bridge.embedded ? null : deps.direct?.load();
          let restored: Session | null = null;
          if (saved) {
            try {
              const read = await deps.direct!.restore(saved);
              if (!read.session) {
                setState({ status: "account", account: read });
                return;
              }
              restored = read.session;
            } catch {
              deps.direct!.save(null);
            }
          }
          // The tab's own session unless a notice for another business was tapped; then that business's way in, if this browser has one.
          const session = restored && (!wantedTenant || restored.tenant.id === wantedTenant) ? restored : ((await openDevice(wantedTenant)) ?? restored);
          setState(session ? { status: "ready", session } : { status: "none" });
          return;
        }
        await adopt(first);
      } catch (err) {
        setState({ status: "error", code: err instanceof ApiError ? err.code : "unknown" });
      }
    },
    refreshAfterUnauthorized: () => refresh("expired"),
    /** „Încearcă din nou” on a reload that could not reach the API. */
    retryResume(): void {
      if (state.status === "unreachable") void resumeFromCookie();
    },
    /** After the code or the link: a session in a workspace, or only the account, before its first workspace. */
    adoptSignedIn(signedIn: AccountSignedIn) {
      if (deps.bridge.embedded) return;
      adoptSignedIn(signedIn);
    },
    /**
     * After a change of workspace or a new one, an invitation or a handoff. Only when not embedded: a host owns its own frame's session.
     * `sameAccount` keeps a session that stays signed in so: the new token is the same device's.
     */
    adoptDirect(session: Session, options: { sameAccount?: boolean } = {}) {
      if (deps.bridge.embedded) return;
      if (persistent && options.sameAccount) {
        deps.staying?.tabTenant.save(session.tenant.id);
        scheduleRenewal(session);
      } else {
        persistent = false;
        clearTimeout(renewTimer);
        deps.direct?.save(session.token);
      }
      setState({ status: "ready", session });
    },
    /** An agency person opening another of its clients, inside a host's frame too. */
    adoptOpened(session: Session) {
      if (deps.bridge.embedded) {
        openedTenantId = session.tenant.id;
        scheduleRenewal(session);
        setState({ status: "ready", session });
        return;
      }
      if (persistent) {
        deps.staying?.tabTenant.save(session.tenant.id);
        scheduleRenewal(session);
      } else deps.direct?.save(session.token);
      setState({ status: "ready", session });
    },
    signOut() {
      clearTimeout(resumeTimer);
      persistent = false;
      deps.staying?.hint(false);
      deps.staying?.tabTenant.save(null);
      deps.direct?.save(null);
      clearTimeout(renewTimer);
      setState({ status: "none" });
    },
    /** Whether this session came from signing in here. */
    isDirect: () => !deps.bridge.embedded && (persistent || Boolean(deps.direct?.load())),
    /** Whether this browser stays signed in through the cookie, and so has devices to list. */
    isPersistent: () => persistent,
    stop() {
      clearTimeout(renewTimer);
      clearTimeout(resumeTimer);
    },
  };
}

export type SessionStore = ReturnType<typeof createSessionStore>;

const TokenHints = z.object({ lang: UiLang.optional(), theme: Theme.optional() });

/**
 * Reads language and theme from the token without verifying it, only so the first paint matches the host while the
 * exchange runs. Nothing is decided from this: the verified values replace it when the exchange returns.
 */
export function peekTokenHints(token: string): z.output<typeof TokenHints> {
  try {
    const payload = token.split(".")[1] ?? "";
    const json = new TextDecoder().decode(Uint8Array.from(atob(payload.replace(/-/g, "+").replace(/_/g, "/")), (ch) => ch.charCodeAt(0)));
    const parsed = TokenHints.safeParse(JSON.parse(json));
    return parsed.success ? parsed.data : {};
  } catch {
    return {};
  }
}
